import React, { useState } from 'react';
import { Modal } from './Modal';
import { MapPin, Mail, Shield, FileText, MessageCircle } from 'lucide-react';
export function Footer() {
  const [activeModal, setActiveModal] = useState<'privacy' | 'terms' | 'contact' | null>(null);
  const currentYear = new Date().getFullYear();
  const closeModal = () => setActiveModal(null);
  return <footer className="relative bg-slate-900 text-slate-400 pt-16 pb-8 overflow-hidden border-t border-yellow-500/20" data-id="element-107">
      {/* Decorative Top Gradient */}
      <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-purple-900 via-yellow-500 to-purple-900 opacity-50" data-id="element-108"></div>

      <div className="max-w-7xl mx-auto px-6 relative z-10" data-id="element-109">
        <div className="flex flex-col items-center text-center mb-12" data-id="element-110">
          {/* Logo */}
          <div className="mb-8" data-id="element-111">
            <h2 className="text-3xl font-extrabold text-white tracking-tight" data-id="element-112">
              Win<span className="text-yellow-500" data-id="element-113">Lords</span>
              <span className="text-slate-500 text-lg font-normal" data-id="element-114">.com</span>
            </h2>
            <div className="flex justify-center gap-4 mt-2 text-yellow-500/40 text-sm" data-id="element-115">
              <span data-id="element-116">♠</span>
              <span data-id="element-117">♥</span>
              <span data-id="element-118">♣</span>
              <span data-id="element-119">♦</span>
            </div>
          </div>

          {/* Navigation Links */}
          <nav className="flex flex-wrap justify-center gap-8 mb-8" data-id="element-120">
            <button onClick={() => setActiveModal('privacy')} className="text-slate-400 hover:text-yellow-500 transition-colors font-medium flex items-center gap-2 group" data-id="element-121">
              <Shield className="w-4 h-4 group-hover:text-yellow-500 transition-colors" data-id="element-122" />
              Privacy Policy
            </button>
            <button onClick={() => setActiveModal('terms')} className="text-slate-400 hover:text-yellow-500 transition-colors font-medium flex items-center gap-2 group" data-id="element-123">
              <FileText className="w-4 h-4 group-hover:text-yellow-500 transition-colors" data-id="element-124" />
              Terms of Use
            </button>
            <button onClick={() => setActiveModal('contact')} className="text-slate-400 hover:text-yellow-500 transition-colors font-medium flex items-center gap-2 group" data-id="element-125">
              <MessageCircle className="w-4 h-4 group-hover:text-yellow-500 transition-colors" data-id="element-126" />
              Contact Us
            </button>
          </nav>
        </div>

        {/* Copyright */}
        <div className="border-t border-slate-800 pt-8 flex flex-col md:flex-row justify-between items-center gap-4 text-sm text-slate-500" data-id="element-127">
          <p data-id="element-128">© {currentYear} WinLords.com. All rights reserved.</p>
          <p data-id="element-129">Play Responsibly. 19+</p>
        </div>
      </div>

      {/* Modals */}
      <Modal isOpen={activeModal === 'privacy'} onClose={closeModal} title="Privacy Policy" data-id="element-130">
        <div className="space-y-4" data-id="element-131">
          <p data-id="element-132">
            At WinLords.com, we take your privacy seriously. This Privacy Policy
            outlines how we collect, use, and protect your personal information
            when you visit our website.
          </p>

          <h4 className="text-white font-bold text-lg mt-6 mb-2" data-id="element-133">
            Information Collection
          </h4>
          <p data-id="element-134">
            We may collect non-personal information about your visit, such as
            your IP address, browser type, and referring pages. This data is
            used solely for analytics purposes to improve our content and user
            experience.
          </p>

          <h4 className="text-white font-bold text-lg mt-6 mb-2" data-id="element-135">Cookies</h4>
          <p data-id="element-136">
            We use cookies to enhance your browsing experience. These small text
            files are stored on your device to remember your preferences. You
            can disable cookies in your browser settings, though this may affect
            site functionality.
          </p>

          <h4 className="text-white font-bold text-lg mt-6 mb-2" data-id="element-137">
            Third-Party Services
          </h4>
          <p data-id="element-138">
            We may use third-party services for analytics or advertising. These
            services have their own privacy policies, and we encourage you to
            review them.
          </p>
        </div>
      </Modal>

      <Modal isOpen={activeModal === 'terms'} onClose={closeModal} title="Terms of Use" data-id="element-139">
        <div className="space-y-4" data-id="element-140">
          <p data-id="element-141">
            Welcome to WinLords.com. By accessing this website, you agree to be
            bound by these Terms of Use.
          </p>

          <h4 className="text-white font-bold text-lg mt-6 mb-2" data-id="element-142">
            Informational Purpose Only
          </h4>
          <p data-id="element-143">
            The content provided on WinLords.com is for informational and
            entertainment purposes only. We are not a gambling operator and do
            not offer real money gambling services. The information provided
            does not constitute legal or financial advice.
          </p>

          <h4 className="text-white font-bold text-lg mt-6 mb-2" data-id="element-144">
            Responsible Gambling
          </h4>
          <p data-id="element-145">
            Gambling involves risk. Please gamble responsibly and only bet what
            you can afford to lose. If you or someone you know has a gambling
            problem, please seek help from professional organizations.
          </p>

          <h4 className="text-white font-bold text-lg mt-6 mb-2" data-id="element-146">
            Age Restriction
          </h4>
          <p data-id="element-147">
            This website is intended for users aged 19 and older (or the legal
            gambling age in your jurisdiction). By using this site, you confirm
            that you meet the legal age requirements.
          </p>
        </div>
      </Modal>

      <Modal isOpen={activeModal === 'contact'} onClose={closeModal} title="Contact Us" data-id="element-148">
        <div className="space-y-6" data-id="element-149">
          <p className="text-lg" data-id="element-150">
            Have questions or feedback? We'd love to hear from you. Reach out to
            our team using the information below.
          </p>

          <div className="bg-slate-800/50 p-6 rounded-lg border border-slate-700 space-y-6" data-id="element-151">
            <div className="flex items-start gap-4" data-id="element-152">
              <div className="p-3 bg-yellow-500/10 rounded-full text-yellow-500" data-id="element-153">
                <MapPin className="w-6 h-6" data-id="element-154" />
              </div>
              <div data-id="element-155">
                <h4 className="text-white font-bold mb-1" data-id="element-156">Mailing Address</h4>
                <p className="text-slate-300" data-id="element-157">WinLords.com</p>
                <p className="text-slate-300" data-id="element-158">455 E Eisenhower Pkwy</p>
                <p className="text-slate-300" data-id="element-159">Ann Arbor, MI 48108</p>
                <p className="text-slate-300" data-id="element-160">United States</p>
              </div>
            </div>

            <div className="flex items-start gap-4" data-id="element-161">
              <div className="p-3 bg-yellow-500/10 rounded-full text-yellow-500" data-id="element-162">
                <Mail className="w-6 h-6" data-id="element-163" />
              </div>
              <div data-id="element-164">
                <h4 className="text-white font-bold mb-1" data-id="element-165">Email Support</h4>
                <p className="text-slate-300" data-id="element-166">contact@winlords.com</p>
                <p className="text-sm text-slate-500 mt-1" data-id="element-167">
                  We typically respond within 24-48 hours.
                </p>
              </div>
            </div>
          </div>
        </div>
      </Modal>
    </footer>;
}